import { DayColumn as WizardDayColumn, DeliveryCard as WizardDeliveryCard } from '../types/wizard';

/**
 * Library types for SalesStackedSchedule (re-exported from the library)
 */
export interface LibraryDeliveryCard {
  id: string;
  ordem: string;
  item: string;
  centro: string;
  cli_cod_pai: number;
  cli_razao_social: string;
  no_vendedor: string;
  cd_produto: string;
  prd_dc_linha: string;
  prd_espessura: number;
  prd_largura?: number;
  prd_comprimento?: number;
  prd_tx_breve: string;
  qt_vol_m3_liquido_gerencial: number;
  dt_emissao: string;
  dt_preparacao_material?: string;
  dt_producao: string; // Can be empty string for unprioritized orders
  request_uuid?: string;
  prd_confirmed?: boolean;
  parent_id?: string;
  
  // Computed/display properties for backward compatibility
  productId: string;
  productName: string;
  units: number;
  client: string;
  
  // Legacy fields for backward compatibility
  dt_desejada_remessa?: string;
}

export interface LibraryDayColumn {
  date: string;
  deliveries: LibraryDeliveryCard[];
}

/**
 * Convert wizard data format to library data format
 * This function handles the conversion between simplified wizard format
 * and the full library format expected by SalesStackedSchedule
 * Returns both the converted schedule and all orders for unprioritized handling
 */
export const convertWizardToLibraryFormat = (
  wizardData: WizardDayColumn[]
): { schedule: LibraryDayColumn[]; allOrders: LibraryDeliveryCard[] } => {
  const allOrders: LibraryDeliveryCard[] = [];
  const scheduleMap = new Map<string, LibraryDeliveryCard[]>();

  // Process all deliveries
  wizardData.forEach(day => {
    day.deliveries.forEach((delivery, index) => {
      // Check if this delivery has a valid production date BEFORE conversion
      const hasValidProductionDate = delivery.dt_producao && 
                                   delivery.dt_producao !== '' && 
                                   delivery.dt_producao !== 'null' &&
                                   delivery.dt_producao !== null &&
                                   delivery.dt_producao !== undefined;

      const convertedCard = convertDeliveryCard(delivery, day.date, index);
      
      if (hasValidProductionDate && convertedCard.dt_producao !== '') {
        // Add to schedule
        if (!scheduleMap.has(convertedCard.dt_producao)) {
          scheduleMap.set(convertedCard.dt_producao, []);
        }
        scheduleMap.get(convertedCard.dt_producao)!.push(convertedCard);
      } else {
        // Add to unprioritized orders
        allOrders.push(convertedCard);
      }
    });
  });

  // Convert map to array format
  const schedule: LibraryDayColumn[] = Array.from(scheduleMap.entries()).map(([date, deliveries]) => ({
    date,
    deliveries
  }));

  // Sort by date
  schedule.sort((a, b) => a.date.localeCompare(b.date));

  return { schedule, allOrders };
};



/**
 * Convert a single wizard delivery card to library format
 */
const convertDeliveryCard = (
  wizardCard: WizardDeliveryCard, 
  date: string, 
  index: number
): LibraryDeliveryCard => {
  // Use original data from the payload, with fallbacks only for missing fields
  const baseId = wizardCard.id || `${date}-${index}`;
  const ordem = wizardCard.ordem || `108${String(Math.floor(Math.random() * 9000) + 1000)}`;
  const item = wizardCard.item || `${(Math.floor(Math.random() * 5) + 1) * 10}`;
  const centro = wizardCard.centro || 'D063';
  const cd_produto = wizardCard.cd_produto || `1008${String(Math.floor(Math.random() * 9000) + 1000)}`;
  
  // Use original product data if available
  const prd_dc_linha = wizardCard.prd_dc_linha || 'MDP MADEPAN';
  const prd_espessura = wizardCard.prd_espessura || wizardCard.espessura || 12;
  // Parse dimensions - they come as strings from CSV but need to be numbers
  const prd_largura = wizardCard.largura || parseFloat(wizardCard.prd_largura || '0') || 0;
  const prd_comprimento = wizardCard.comprimento || parseFloat(wizardCard.prd_comprimento || '0') || 0;
  
  // Debug log to verify dimensions are being parsed correctly
  if (process.env.NODE_ENV === 'development' && (prd_largura > 0 || prd_comprimento > 0)) {
    console.log('🔧 Dimension conversion:', {
      cardId: baseId,
      cliente: wizardCard.cli_razao_social,
      original_largura: wizardCard.prd_largura,
      original_comprimento: wizardCard.prd_comprimento,
      parsed_largura: prd_largura,
      parsed_comprimento: prd_comprimento
    });
  }
  const prd_tx_breve = `P MP-SUPIN ${prd_espessura.toFixed(1)} 1850 2750 00044`;
  
  // Use original dates from the payload
  const dt_emissao = wizardCard.dt_emissao || '';
  const dt_preparacao_material = wizardCard.dt_preparacao_material || date;
  const dt_producao = wizardCard.dt_producao || ''; // Keep empty if no production date
  
  // Use original volume data
  const qt_vol_m3_liquido_gerencial = wizardCard.volume || wizardCard.units || 0;
  
  // Use original client and vendor data
  const cli_razao_social = wizardCard.cli_razao_social || wizardCard.cliente || '';
  const no_vendedor = wizardCard.vendedor || '';
  
  return {
    id: baseId,
    ordem,
    item,
    centro,
    cli_cod_pai: wizardCard.cli_cod_pai || 0,
    cli_razao_social,
    no_vendedor,
    cd_produto,
    prd_dc_linha,
    prd_espessura,
    prd_largura,
    prd_comprimento,
    prd_tx_breve,
    qt_vol_m3_liquido_gerencial,
    dt_emissao,
    dt_preparacao_material,
    dt_producao,
    parent_id: baseId,
    
    // Computed properties
    productId: cd_produto,
    productName: prd_tx_breve,
    units: qt_vol_m3_liquido_gerencial,
    client: cli_razao_social,
  };
};

/**
 * Get month name from schedule data
 */
export const getMonthNameFromSchedule = (_scheduleData: WizardDayColumn[]): string => {
  return 'Priorização de Vendas';
}; 